import { Round } from "@core-ts/Math"
import { Context, context, ContextProvider, log, setRootContext } from "./Context"
import { WeakPairList } from "@core-ts/Map/WeakPairList"
import { Instance } from "./Instance"
import { ConfigurationSet } from "@core-ts/Config"
import { rootRunWrapper } from "@core-ts/implementApplication"
import { StorageServiceClient } from "@core-ts/clients/StorageServiceClient"
import { AuthenticationServiceClient } from "@core-ts/clients/AuthenticationServiceClient"
import { LogServiceClient } from "@core-ts/clients/LogServiceClient"
import { StandardError } from "@core-ts/StandardError"
import { Request } from "./Request"
import { VerifiedSession } from "./VerifiedSession"
import { version } from "@core-ts/Version"
import { VersionInfo } from "./VersionInfo"

export abstract class Application<InstanceType extends Instance> {
    constructor(
        readonly type: "frontend" | "gateway" | "service" | "command",
        readonly applicationName: string,
        readonly contextProvider: ContextProvider,
    ) {
        if (this.type === "gateway" || this.type === "service" || this.type === "command") {
            Application.addStatisticsHook(this, () => {
                log.stat(
                    "Uptime: " +
                        Round((Date.now() - this.startedTime) / 1000, 0) +
                        " seconds, Memory usage: " +
                        Round(process.memoryUsage().rss / 1024 / 1024, 0) +
                        " MB",
                )
            })
        }
    }

    readonly version: VersionInfo = version

    abstract get isFrontend(): boolean

    protected startOnlyInstance: string | null = null

    private instanceContexts = new Map<string, InstanceContext<InstanceType>>()
    // If this application only runs a single instance, this is not null and contains the name of that instance. This allows gateways to have a default instance without a custom X-WebApp-Site header
    protected static singleInstanceName: string | null = null

    public startApplication(config: ConfigurationSet): void {
        setRootContext(new Context(this))

        rootRunWrapper(async () => {
            await this.setupApplicationPreInstanceInit()

            const instancesConfig = config.getConfig("instances")
            if(instancesConfig.subConfigs.size === 1) {
                Application.singleInstanceName = instancesConfig.subConfigs.keys().next().value ?? null
            }
            for (const [instanceName, instanceConfig] of instancesConfig.subConfigs) {
                if (this.startOnlyInstance === null || instanceName === this.startOnlyInstance)
                    this.instanceContexts.set(
                        instanceName,
                        await this.setupInstanceContext(instanceName, instanceConfig),
                    )
            }

            await this.runForAllInstancesLogErrors(async () => {
                await this.runInstance()
            })

            await this.setupApplicationPostInstanceInit()
        })
    }

    protected abstract setupInstanceContext(
        instanceName: string,
        config: ConfigurationSet,
    ): Promise<InstanceContext<InstanceType>>

    protected async setupApplicationPreInstanceInit(): Promise<void> {}
    protected async setupApplicationPostInstanceInit(): Promise<void> {}
    //protected async initInstance(): Promise<void> {}
    protected async runInstance(): Promise<void> {}

    public async runForInstance(instanceName: string, fn: () => Promise<void>): Promise<void> {
        if (context.haveInstance) {
            throw new StandardError(
                "runForInstance cannot be called from within an instance context, it is only to be used from the application context",
            )
        }

        const instanceContext = this.instanceContexts.get(instanceName)
        if (instanceContext === undefined) throw new Error("Instance " + instanceName + " not found")

        await context.createLocalContext(async () => {
            context.instance = instanceContext.instance
            context.config = instanceContext.config
            if (instanceContext.storage !== undefined) context.storage = instanceContext.storage
            if (instanceContext.authentication !== undefined) context.authentication = instanceContext.authentication
            if (instanceContext.logger !== undefined) context.logger = instanceContext.logger
            if (instanceContext.verifiedSession !== undefined) context.verifiedSession = instanceContext.verifiedSession
            if (instanceContext.request !== undefined) context.request = instanceContext.request

            await fn()
        })
    }

    public async runForAllInstances(fn: () => Promise<void>): Promise<void> {
        for (const instanceName of this.instanceContexts.keys()) {
            await this.runForInstance(instanceName, fn)
        }
    }

    public async runForAllInstancesLogErrors(fn: () => Promise<void>): Promise<void> {
        for (const instanceName of this.instanceContexts.keys()) {
            try {
                await this.runForInstance(instanceName, fn)
            } catch (err) {
                if (err instanceof Error)
                    log.error("Error in runForAllInstances for instance " + instanceName + ": ", err)
                else log.error("Error in runForAllInstances for instance " + instanceName)
            }
        }
    }

    private startedTime = Date.now()

    // This is strange construction, each
    private static statisticsHooks: WeakPairList<object, () => void> = new WeakPairList()

    static addStatisticsHook<T>(obj: object, hook: () => void): void {
        this.statisticsHooks.push(obj, hook)
    }

    private static garbageCollectHook: WeakPairList<object, () => void> = new WeakPairList()
    private static garbageCollectHookPerInstance: WeakPairList<object, () => void> = new WeakPairList()
    static addGarbageCollectHook(obj: object, hook: () => void): void {
        this.garbageCollectHook.push(obj, hook)
    }

    static addGarbageCollectHookPerInstance(obj: object, hook: () => void): void {
        this.garbageCollectHookPerInstance.push(obj, hook)
    }

    // These should never be run by the user, they are called by the framework
    runGarbageCollectHooks(): void {
        Application.garbageCollectHook.forEach((obj, hook) => {
            try {
                hook()
            } catch (err) {
                if (err instanceof Error) log.error("Error in garbageCollectHook hook: ", err)
                else log.error("Error in garbageCollectHook hook")
            }
        })

        Application.garbageCollectHookPerInstance.forEach(async (obj, hook) => {
            await this.runForAllInstancesLogErrors(async () => {
                hook()
            })
        })
    }

    // These should never be run by the user, they are called by the framework
    runStatisticsHooks(): void {
        Application.statisticsHooks.forEach((obj, hook) => {
            try {
                hook()
            } catch (err) {
                if (err instanceof Error) log.error("Error in statistics hook: ", err)
                else log.error("Error in statistics hook")
            }
        })
    }
}

export interface InstanceContext<InstanceType extends Instance> {
    instance: InstanceType
    config: ConfigurationSet
    storage?: StorageServiceClient
    authentication?: AuthenticationServiceClient
    logger?: LogServiceClient
    verifiedSession?: VerifiedSession
    request?: Request
}
